import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Power, QrCode, Landmark, CreditCard, DollarSign, Euro, Bitcoin, Save, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ITEMS = [
  { key: "pagarme_pix", icon: QrCode, title: "Pix automático (Pagar.me)", desc: "QR Code gerado pela Pagar.me; saldo cai automaticamente via webhook.", cfg: (m) => m.pagarme_configured },
  { key: "manual_pix", icon: Landmark, title: "Pix manual (chave do admin)", desc: "Usuário paga na sua chave, envia o comprovante e você confirma aqui.", cfg: (m) => m.manual_pix_configured },
  { key: "card", icon: CreditCard, title: "Cartão de crédito (Pagar.me)", desc: "Depósitos, boosts, contratações e chamadas com cartão salvo.", cfg: (m) => m.pagarme_configured },
  { key: "fx_usd", icon: DollarSign, title: "Dólar (Stripe)", desc: "Cartão internacional em US$; convertido para R$ pela cotação do dia.", cfg: (m) => m.stripe_configured },
  { key: "fx_eur", icon: Euro, title: "Euro (Stripe)", desc: "Cartão internacional em €; convertido para R$ pela cotação do dia.", cfg: (m) => m.stripe_configured },
  { key: "crypto", icon: Bitcoin, title: "Criptomoedas (CoinGate)", desc: "USDT, BTC, ETH e outras; crédito após confirmação na blockchain.", cfg: (m) => m.crypto_configured },
];

function FxCryptoSettings({ onSaved }) {
  const [s, setS] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/payments/fx-settings`).then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const body = { fx_margin_pct: parseFloat(s.fx_margin_pct) || 0, coingate_mode: s.coingate_mode, coingate_receive_currency: s.coingate_receive_currency };
      if (token.trim()) body.coingate_token = token.trim();
      const r = await axios.put(`${API}/admin/payments/fx-settings`, body);
      setS(r.data); setToken("");
      toast.success("Configurações salvas");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    setBusy(false);
  };
  if (!s) return null;
  return (
    <div className="mt-4 rounded-xl border p-3 grid md:grid-cols-4 gap-3 items-end" data-testid="fx-crypto-settings">
      <div>
        <Label className="text-xs">Taxa sobre a cotação (%)</Label>
        <Input type="number" min={0} max={20} step="0.1" value={s.fx_margin_pct} onChange={(e) => setS({ ...s, fx_margin_pct: e.target.value })} data-testid="fx-margin-input" />
        <p className="text-[10px] text-muted-foreground mt-1">0 = cotação do dia sem margem.</p>
      </div>
      <div>
        <Label className="text-xs">Chave API CoinGate {s.coingate_configured && <span className="text-emerald-600">· {s.coingate_token_masked}</span>}</Label>
        <Input type="password" placeholder={s.coingate_configured ? "Colar nova chave para substituir" : "Cole a chave do CoinGate"} value={token} onChange={(e) => setToken(e.target.value)} data-testid="coingate-token-input" />
      </div>
      <div>
        <Label className="text-xs">Ambiente CoinGate</Label>
        <div className="inline-flex rounded-full bg-muted p-0.5 text-xs w-full">
          {[["sandbox", "Sandbox (teste)"], ["live", "Produção"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setS({ ...s, coingate_mode: k })} data-testid={`coingate-mode-${k}`} className={`flex-1 px-3 py-1.5 rounded-full ${s.coingate_mode === k ? "bg-background shadow font-semibold" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={busy} className="rounded-full" data-testid="fx-crypto-save">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Salvar</Button>
    </div>
  );
}

export default function AdminPaymentMethodsCard() {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => axios.get(`${API}/payments/methods`).then(r => setM(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (key, value) => {
    setBusy(key);
    try {
      const r = await axios.put(`${API}/admin/payments/methods`, { [key]: value });
      setM(prev => ({ ...prev, ...r.data }));
      toast.success(`${ITEMS.find(i => i.key === key).title} ${value ? "ligado" : "desligado"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao atualizar"); }
    setBusy("");
  };

  if (!m) return null;
  return (
    <Card className="p-4" data-testid="admin-payment-methods">
      <div className="flex items-center gap-2 mb-3">
        <Power className="w-4 h-4 text-primary" />
        <h3 className="font-bold">Métodos de pagamento</h3>
        <span className="text-xs text-muted-foreground">· desligue um método se ele estiver com problema</span>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {ITEMS.map(({ key, icon: Icon, title, desc, cfg }) => {
          const on = !!m[key];
          const configured = cfg(m);
          return (
            <div key={key} className={`rounded-xl border p-3 flex flex-col gap-2 ${on ? "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/20" : "border-border opacity-80"}`} data-testid={`payment-method-${key}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2"><Icon className="w-4 h-4" /><span className="text-sm font-semibold">{title}</span></div>
                <Switch checked={on} disabled={busy === key} onCheckedChange={(v) => toggle(key, v)} data-testid={`payment-method-toggle-${key}`} aria-label={title} />
              </div>
              <p className="text-[11px] text-muted-foreground">{desc}</p>
              <div className="flex gap-1">
                <Badge variant={on ? "default" : "secondary"} className="text-[10px]" data-testid={`payment-method-status-${key}`}>{on ? "Ligado" : "Desligado"}</Badge>
                {!configured && <Badge variant="outline" className="text-[10px] text-amber-700">{key === "crypto" ? "aguardando chave" : "não configurado"}</Badge>}
              </div>
            </div>
          );
        })}
      </div>
      <FxCryptoSettings onSaved={load} />
    </Card>
  );
}
